import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Volume2, PenLine, ChevronRight, Image } from "lucide-react";
import { asset } from "../../lib/api";
import { play, preload, sfx } from "../../lib/audio";
import { HanziTrace } from "./HanziTrace";

const CharCard = ({ c, onTraced }) => {
  const [trace, setTrace] = useState(false);
  useEffect(() => { setTrace(false); play(c.traditional_character, c.audio_url); }, [c]);
  return (
    <div className="grid md:grid-cols-2 gap-8 items-center w-full max-w-5xl" data-testid={`char-card-${c.traditional_character}`}>
      <div className="flex flex-col items-center gap-4">
        <motion.button key={c.character_id} data-testid="char-big-symbol" onClick={() => play(c.traditional_character, c.audio_url)}
          initial={{ scale: 0.4 }} animate={{ scale: 1 }} transition={{ type: "spring" }} whileTap={{ scale: 0.92 }}
          className="relative w-60 h-60 sm:w-72 sm:h-72 rounded-[2.5rem] bg-[#FFFDF7] border-4 border-[var(--vermilion)] shadow-xl flex items-center justify-center gap-2">
          <span className="font-serif-tc text-[8rem] sm:text-[10rem] font-black text-red-950 leading-none">{c.traditional_character}</span>
          <span className="zhuyin-vert text-3xl sm:text-4xl font-bold text-[var(--vermilion)]">{c.zhuyin}</span>
        </motion.button>
        <div className="flex gap-3">
          <button data-testid="char-repeat-btn" onClick={() => play(c.traditional_character, c.audio_url)} className="kid-btn px-5 bg-amber-100 text-[var(--tea)] flex items-center gap-2"><Volume2 /> 再聽</button>
          <button data-testid="char-trace-toggle" onClick={() => setTrace(!trace)} className="kid-btn px-5 bg-[var(--jade)] text-white flex items-center gap-2">
            {trace ? <><Image /> 看圖</> : <><PenLine /> 寫一寫</>}
          </button>
        </div>
      </div>
      {trace ? (
        <HanziTrace char={c.traditional_character} onComplete={onTraced} />
      ) : (
        <motion.div initial={{ opacity: 0, x: 30 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.3 }} className="scroll-card p-6 flex flex-col items-center gap-4">
          <div className="text-[7rem] leading-none">{c.image_url ? <img src={asset(c.image_url)} alt="" className="h-36" /> : c.image_asset}</div>
          <button data-testid="char-example-word-btn" onClick={() => play(c.example_word)} className="font-serif-tc text-5xl font-black text-[var(--tea)] px-6 py-2 rounded-2xl hover:bg-amber-50">{c.example_word}</button>
          <button data-testid="char-example-sentence-btn" onClick={() => play(c.example_sentence)} className="font-serif-tc text-2xl text-stone-700 flex items-center gap-2 hover:text-[var(--vermilion)]">
            <Volume2 size={20} /> {c.example_sentence}
          </button>
        </motion.div>
      )}
    </div>
  );
};

export const CharacterStep = ({ list, onSeen, onTraced, onDone }) => {
  const [i, setI] = useState(0);
  useEffect(() => {
    preload(list.flatMap((c) => [c.traditional_character, c.example_word]));
    onSeen(list.map((c) => ({ type: "character", key: c.traditional_character })));
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  if (!list.length) { onDone(); return null; }
  const next = () => { sfx("tap"); if (i < list.length - 1) setI(i + 1); else onDone(); };
  return (
    <div className="flex flex-col items-center justify-between h-full gap-6 py-4" data-testid="character-step">
      <div className="flex gap-2">
        {list.map((c, k) => (
          <button key={c.character_id} data-testid={`char-tab-${k}`} onClick={() => setI(k)}
            className={`w-12 h-12 rounded-xl font-serif-tc text-2xl font-bold border-2 ${k === i ? "bg-[var(--vermilion)] text-white border-transparent" : "bg-white border-[var(--line)]"}`}>{c.traditional_character}</button>
        ))}
      </div>
      <div className="flex-1 flex items-center justify-center w-full"><CharCard c={list[i]} onTraced={() => onTraced(list[i].traditional_character)} /></div>
      <button data-testid="char-next-btn" onClick={next} className="kid-btn px-10 bg-[var(--vermilion)] text-white text-2xl font-serif-tc flex items-center gap-2">
        {i < list.length - 1 ? "下一個字" : "去玩遊戲"} <ChevronRight />
      </button>
    </div>
  );
};
